import { useRef, useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PopulationBasedFundingBasePopupBody } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingBasePopupBody";
import { useOverlay } from "@/Common/CustomHooks/useOverlay";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingFormData, PopulationBasedFundingFormEnum } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PopulationBasedFundingEventAction } from "@/Common/Models/UI/Enums";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingPopUp.css";

interface IPopulationBasedFundingPatientHistoryItemEditPopupProps {
    item: PopulationBasedFundingEventWithNotesModel;
    patientModel: Patient;
    onCloseCallback: () => void;
}

function PopulationBasedFundingPatientHistoryItemEditPopup(props: IPopulationBasedFundingPatientHistoryItemEditPopupProps): JSX.Element {
    const { getText } = useTranslation();
    useOverlay("populationBasedFunding-edit-popup-overlay");

    const [formData, setFormData] = useState<PopulationBasedFundingFormData>(() => new PopulationBasedFundingFormData({
        SelectedPhysician: cacheStore.GetCliniciansPersonsByContactFacetId(props.item.EmployeeContactFacetId),
        SelectedEffectiveDate: props.item.EffectiveDate,
        Notes: "",
        NotesHistory: props.item.Notes.map(p => cacheStore.GetEmployeeByUserId(String(p.CreatedBy)).UserName + " (" + LuxonDateTimeParser.parse(p.CreatedDate)?.toFormat("YYYY/MM/DD HH:mm:ss") + "): " + p?.Content),
        ActionEnum: props.item.ActionEnum,
        SelectedDeregisterReason: props.item.DeregisteredReasonEnum,
        DeregistrationOrProposalCustomReason: props.item.DeregistrationOrProposalCustomReason,
        WillContinueToSeePhysician: props.item.WillContinueTreatment,
        AdministrativeCode: props.item.AdministrativeCode
    }));

    const [hasSaveButtonBeenClicked, setHasSaveButtonBeenClicked] = useState(false);

    const isServerCallAlreadySent = useRef(false);

    const formType = props.item.ActionEnum == PopulationBasedFundingEventAction.Register ? PopulationBasedFundingFormEnum.Register : PopulationBasedFundingFormEnum.Deregister;

    const onSave = async (form: PopulationBasedFundingFormData): Promise<void> => {
        await PopulationBasedFundingActions.editEvent(
            props.item.Id,
            form.SelectedPhysician.ContactFacetId,
            form.SelectedEffectiveDate,
            form.Notes,
            form.AdministrativeCode,
            form.SelectedDeregisterReason,
            form.DeregistrationOrProposalCustomReason,
            form.WillContinueToSeePhysician
        );

        PopulationBasedFundingActions.loadPatientHistoryAndExpandAll(props.patientModel);
        PopulationBasedFundingActions.updateIcon(props.patientModel.InactivableResponse.Id);
        props.onCloseCallback();
    };

    const onSaveButtonClicked = async (form: PopulationBasedFundingFormData): Promise<void> => {
        setHasSaveButtonBeenClicked(true);

        if (!isServerCallAlreadySent.current) {
            isServerCallAlreadySent.current = true;
            await onSave(form);
        }
    };

    function renderFooter(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="footer-button-container">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => props.onCloseCallback()}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        id="save_registration_butt"
                        className="btn btn-primary"
                        label={getText("SaveLabel")}
                        onClick={() => onSaveButtonClicked(formData)}
                        disabled={hasSaveButtonBeenClicked || formData.SelectedPhysician === null || formData.SelectedEffectiveDate === null}
                    />
                </div>
            </div>
        );
    }

    return (
        <Modal
            id="PopulationBasedFundingRegistrationPopup"
            title={getText("PopulationBasedFunding_EditPatientRegistration")}
            onClose={() => props.onCloseCallback()}
            top={50}
            width={485}
            draggable
            showOverlay={true}
            footer={renderFooter()}
            showX={true}
            roundTopCorner={true}
            disableOverlayClose={true}
        >
            <PopulationBasedFundingBasePopupBody
                formData={formData}
                onChange={formData => setFormData(formData)}
                formType={formType}
            />
        </Modal>
    );
}

export { PopulationBasedFundingPatientHistoryItemEditPopup };
